import { apiFetch, apiFetchBytes } from './client.js';
import { isEvidenceImage } from './evidenceFiles.js';

// R11 — analyst Intake queue client. The customer-facing submission endpoints
// (POST/GET /intake/submissions) are called by external client-credentials
// integrations, NOT the SPA; the SPA only ever talks to the staff queue
// endpoints below.

// The submission payload shape (mirrors the API's strict body schema). Kept
// loose on the CTIA identifier fields since they're optional at intake.
export interface IntakeSubmissionPayload {
  legalName: string;
  fein: string;
  website?: string;
  orgType: 'A' | 'B' | 'C';
  jurisdiction: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  /**
   * #685 — the billing address is four fields, matching the registered address
   * above. `address` is the pre-#685 single line: the API refuses it on a NEW
   * submission and every stored one filed before the split still carries it, so
   * the type keeps both and the queue detail renders whichever is present.
   */
  billingContact: {
    name: string; email: string; phone?: string; title?: string;
    street?: string; city?: string; state?: string; zip?: string;
    /** LEGACY, read-only: applications filed before the address was split. */
    address?: string;
  };
  authorizerContact: { name: string; email: string; phone?: string; title?: string };
  displayName: string;
  callReason: string;
  callLogoUrl?: string;
  ospids?: string[];
  dirid?: string;
  eid?: string;
  originatingCarrier?: string;
  /** #775 — the customer declared numbers 382 does not provide; the LOA is on the submission. */
  notProvidedBy382?: boolean;
}

// WITHDRAWN is customer-initiated (Customer API v2, #147); a withdrawn
// submission leaves the analyst queue but can still appear under the staff
// "All" filter, so the SPA must render it.
export type IntakeStatus = 'PENDING' | 'PROMOTED' | 'REJECTED' | 'WITHDRAWN';

export interface IntakeSubmission {
  id: string;
  customerId: string;
  status: IntakeStatus;
  payload: IntakeSubmissionPayload;
  analystFeedback: string | null;
  promotedCaseId: string | null;
  /**
   * #715 — when the promoted case was DELETED, or null while it is live.
   *
   * `promotedCaseId` cannot answer that on its own. It is a stored column on
   * the submission and `DELETE /api/v1/cases/:id` is a soft delete on the case,
   * so the id reads back identically forever — which is right, the submission
   * was promoted into that case and the row is the company's filing history,
   * but it leaves the Case link pointing at something the server will no longer
   * serve. #691 marked the cell from what the page had just done and forgot on
   * reload; the server answers it now (`withPromotedCaseDeletedAt`,
   * apps/api/src/routes/intake.ts).
   *
   * Optional for the same reason `supersedesId` above is: this interface is the
   * shape of a response the server always sends, and requiring it would make
   * every hand-built fixture in the SPA a compile error without making a single
   * render safer.
   */
  promotedCaseDeletedAt?: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  /**
   * #473 — the RETURNED submission this one corrects, or null.
   *
   * The customer set it when they filed this application; nothing about the row
   * it names changed. REJECTED is terminal, so a correction is always a NEW
   * application rather than a reopening of the old one.
   */
  supersedesId?: string | null;
  /**
   * The returned submission itself, resolved server-side (`withSupersedes` in
   * routes/intake-queue.ts) so the detail panel can show WHEN it was returned
   * and WHAT we told them.
   *
   * Resolved on the server rather than looked up in the already-loaded page,
   * because the queue list is paged and filterable and the filter that hides
   * the returned row most reliably is `status=PENDING` — exactly the one a
   * reviewer works corrections from. Null when this corrects nothing.
   */
  supersedes?: {
    id: string;
    status: IntakeStatus;
    analystFeedback: string | null;
    submittedAt: string;
    /** When the reviewer returned it. */
    reviewedAt: string | null;
  } | null;
}

export interface IntakeQueueParams {
  status?: IntakeStatus;
  limit?: number;
  offset?: number;
}

export interface IntakeQueuePage {
  items: IntakeSubmission[];
  total: number;
}

export async function listIntakeQueue(params: IntakeQueueParams = {}): Promise<IntakeQueuePage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  return apiFetch<IntakeQueuePage>(`/intake/queue${suffix}`);
}

export async function getIntakeSubmission(id: string): Promise<IntakeSubmission> {
  return apiFetch<IntakeSubmission>(`/intake/queue/${id}`);
}

// Promote → creates a DRAFT case (server-side) and marks the submission
// PROMOTED. Returns the new case id so the caller can navigate to it.
export async function promoteIntakeSubmission(
  id: string,
): Promise<{ submissionId: string; caseId: string }> {
  return apiFetch<{ submissionId: string; caseId: string }>(
    `/intake/queue/${id}/promote`,
    { method: 'POST' },
  );
}

export async function rejectIntakeSubmission(
  id: string,
  feedback: string,
): Promise<IntakeSubmission> {
  return apiFetch<IntakeSubmission>(`/intake/queue/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ feedback }),
  });
}

// ---- Customer API v2 (#147): staff TN-change-request attestation queue -----
// The customer-facing create/list of TN change requests is called by external
// client-credentials integrations, not the SPA; the SPA only works the staff
// queue below (approve = the one write that touches the vetted TN list).

export type TnChangeStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface TnChangeRequest {
  id: string;
  customerId: string;
  op: 'A' | 'D';
  tns: string[];
  status: TnChangeStatus;
  submissionId: string | null;
  caseId: string | null;
  note: string | null;
  analystFeedback: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  // #775 — numbers 382 does not provide, and the review of the signed LOA the
  // customer sent for them. All null/false on a request without the flag; the
  // approve route answers 422 on a flagged one until `loaStatus` is VERIFIED.
  notProvidedBy382: boolean;
  loaStatus: LoaStatus | null;
  loaFileId: number | null;
  loaReason: string | null;
  loaReviewedBy: string | null;
  loaReviewedAt: string | null;
}

export type LoaStatus = 'SUBMITTED' | 'NEEDED' | 'VERIFIED';

export interface TnChangeQueueParams {
  status?: TnChangeStatus;
  limit?: number;
  offset?: number;
}

export interface TnChangeQueuePage {
  items: TnChangeRequest[];
  total: number;
}

export async function listTnChangeRequests(params: TnChangeQueueParams = {}): Promise<TnChangeQueuePage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  return apiFetch<TnChangeQueuePage>(`/intake/queue/tn-change-requests${suffix}`);
}

// Approve = attest: applies op:A/op:D to the case's vetted TN list and sets it
// PENDING_SYNC so the #110 settlement propagation delivers it. Returns the
// resulting vetted list plus the (now APPROVED) request.
export async function approveTnChangeRequest(
  id: string,
): Promise<{ requestId: string; caseId: string; deliveredTns: string[] } & TnChangeRequest> {
  return apiFetch(`/intake/queue/tn-change-requests/${id}/approve`, { method: 'POST' });
}

export async function rejectTnChangeRequest(
  id: string,
  feedback: string,
): Promise<TnChangeRequest> {
  return apiFetch<TnChangeRequest>(`/intake/queue/tn-change-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ feedback }),
  });
}

// ---- #775: the customer's signed LOA ----------------------------------------
//
// Customer files are read with the token, as #774's evidence files are: an
// image becomes a `data:` URL for an `<img>` (a bare `<img src>` would carry no
// token, and `blob:` is outside the CSP's img-src), and a PDF is downloaded,
// never rendered in the portal's origin.

/** One file a customer uploaded, as the Intake queue lists it. */
export interface IntakeFile {
  id: number;
  purpose: 'loa';
  filename: string;
  contentType: string;
  byteLength: number;
  sha256: string;
  uploadedAt: string;
  tnChangeRequestId: string | null;
  submissionId: string | null;
  attachedAt: string | null;
}

export type IntakeFileTarget = { tnChangeRequestId: string } | { submissionId: string };

/** Every file attached to a request or application, oldest first: its LOA history. */
export async function listIntakeFiles(target: IntakeFileTarget): Promise<IntakeFile[]> {
  const q = new URLSearchParams(target as Record<string, string>);
  const res = await apiFetch<{ items: IntakeFile[] }>(`/intake/queue/files?${q.toString()}`);
  return res.items;
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('could not read the image'));
    reader.readAsDataURL(blob);
  });
}

/** One customer image as a `data:` URL. Refuses anything but the three image types. */
export async function intakeImageDataUrl(fileId: number): Promise<string> {
  const { bytes, contentType } = await apiFetchBytes(`/intake/queue/files/${fileId}/content`);
  const type = contentType.split(';')[0].trim().toLowerCase();
  if (!isEvidenceImage(type)) throw new Error(`file ${fileId} is not an image (${type || 'no type'})`);
  return readAsDataUrl(new Blob([bytes], { type }));
}

/** Download one customer file through an object URL on an `<a download>` (see downloadEvidenceFile). */
export async function downloadIntakeFile(file: Pick<IntakeFile, 'id' | 'filename'>): Promise<void> {
  const { bytes, contentType } = await apiFetchBytes(`/intake/queue/files/${file.id}/content`);
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType.split(';')[0].trim() }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * The verdict on a TN change request's LOA. `fileId` is the letter the
 * reviewer looked at: the API refuses (409) when the customer has uploaded a
 * newer one since. NEEDED takes the reason the customer is shown.
 */
export async function setTnChangeLoaVerdict(
  id: string,
  verdict: { decision: 'VERIFIED' | 'NEEDED'; fileId: number; reason?: string },
): Promise<TnChangeRequest> {
  return apiFetch<TnChangeRequest>(`/intake/queue/tn-change-requests/${encodeURIComponent(id)}/loa`, {
    method: 'POST',
    body: JSON.stringify(verdict),
  });
}

// ---- #432: company-profile change requests --------------------------------

export type CompanyChangeStatus = 'PENDING' | 'APPLIED' | 'REJECTED' | 'WITHDRAWN';

/** The staff view — every column, including the ones the customer never sees. */
export interface CompanyChangeRequest {
  id: string;
  customerId: string;
  /** The sparse patch of requested values: only the fields being asked about. */
  requested: Record<string, unknown>;
  /**
   * Server-computed. True when the ask includes a legal name or a business
   * address — the two classes that may invalidate the vetting decision already
   * made. Not advisory since #478: accepting such a request writes nothing to
   * the registrations and opens a dual-controlled re-vet on each of them, so
   * the banner it raises is telling the analyst what their click will do.
   */
  requiresRevet: boolean;
  /** #768 — the ONE registration a call-logo change is for; null for every other ask. */
  caseId?: string | null;
  note: string | null;
  status: CompanyChangeStatus;
  staffFeedback: string | null;
  submittedBy: string | null;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

// ---- #478: what accepting will touch, and what it did ----------------------

/** What accepting would do to one of the company's registrations. */
export type RegistrationEffect =
  | 'WRITE' | 'REVET' | 'WRITE_AND_REVET' | 'NO_CHANGE' | 'EXCLUDED' | 'BLOCKED'
  // #768 — a call-logo change, on the one registration it names: re-vetted
  // (it holds an approval) or written (still in vetting).
  | 'LOGO_REVET' | 'LOGO_WRITE';

export interface AffectedRegistration {
  caseId: string;
  registrant: string;
  status: string;
  effect: RegistrationEffect;
  /** The sentence to show. Present on EXCLUDED, BLOCKED and NO_CHANGE. */
  explanation: string | null;
  excludedReason: string | null;
  /** `changeRequestId` is null when the blocking re-vet is a logo change nobody asked for (#768). */
  blockedBy: { changeRequestId: string | null; openedAt: string | null } | null;
}

export interface AffectedRegistrations {
  changeRequestId: string;
  customerId: string;
  status: CompanyChangeStatus;
  requiresRevet: boolean;
  /** #768 — `logo` is the call-logo field, which names `caseId`. */
  fields: { immediate: string[]; revet: string[]; logo?: string[] };
  caseId?: string | null;
  counts: {
    total: number; willWrite: number; willRevet: number;
    noChange: number; excluded: number; blocked: number;
  };
  /** False when something would make the acceptance refuse. */
  acceptable: boolean;
  refusal: string | null;
  ctiaProblem: { field: string; detail: string } | null;
  registrations: AffectedRegistration[];
}

/**
 * What accepting this request would touch, BEFORE anybody clicks accept.
 *
 * A company-level request fans out across every registration the company
 * holds. The reviewer who cannot see that set is not making a decision, they
 * are triggering a fan-out they will find out about afterwards — so the card
 * loads this before it offers the button.
 */
export async function getAffectedRegistrations(id: string): Promise<AffectedRegistrations> {
  return apiFetch<AffectedRegistrations>(
    `/intake/queue/company-change-requests/${id}/affected-registrations`,
  );
}

/** What an acceptance actually did, per registration. */
export interface ApplyPropagation {
  written: string[];
  revetOpened: string[];
  unchanged: string[];
  excluded: AffectedRegistration[];
  customerRecord: { before: string; after: string } | null;
  registrations: AffectedRegistration[];
  /** #768 — the call-logo change: its registration, the snapshot queued, which path. */
  logo?: { caseId: string; snapshotId: number; effect: 'LOGO_REVET' | 'LOGO_WRITE' } | null;
}

export interface CompanyChangeQueueParams {
  status?: CompanyChangeStatus;
  limit?: number;
  offset?: number;
}

export interface CompanyChangeQueuePage {
  items: CompanyChangeRequest[];
  total: number;
}

export async function listCompanyChangeRequests(
  params: CompanyChangeQueueParams = {},
): Promise<CompanyChangeQueuePage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  return apiFetch<CompanyChangeQueuePage>(`/intake/queue/company-change-requests${suffix}`);
}

/**
 * Accept a change request.
 *
 * WHAT IT DOES DEPENDS ON THE FIELD, not on the request (#478). The trading
 * name and the three contacts are written into every in-scope registration as
 * part of accepting. The legal name and the business address write NOTHING:
 * accepting opens a dual-controlled re-vet on each in-scope registration, and
 * the approval of that re-vet is what writes the case and queues the platform
 * sync. One request can carry both halves, and then both happen.
 *
 * The response carries `propagation`, which names the registrations that were
 * written, the ones a re-vet was opened on, and the ones that were skipped —
 * so an acceptance that touched three of five cannot be reported as clean.
 */
export async function applyCompanyChangeRequest(
  id: string,
): Promise<{ request: CompanyChangeRequest; propagation: ApplyPropagation }> {
  // No `staffFeedback` argument. The route accepts an optional one — a caveat
  // recorded alongside an acceptance, which the customer would see — but no
  // surface offers a place to type it, and a parameter no caller can pass reads
  // as a feature that exists. If a "accepted, with a note" flow is wanted, the
  // route is ready and this is where it plugs in.
  return apiFetch<{ request: CompanyChangeRequest; propagation: ApplyPropagation }>(
    `/intake/queue/company-change-requests/${id}/apply`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/** Decline it. The feedback is required and is shown to the customer. */
export async function rejectCompanyChangeRequest(
  id: string,
  staffFeedback: string,
): Promise<CompanyChangeRequest> {
  return apiFetch<CompanyChangeRequest>(`/intake/queue/company-change-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ staffFeedback }),
  });
}

/**
 * The staff-facing labels. Same words the customer's own page uses.
 *
 * Both restate the API's vocabulary, which is canonical (`REQUESTABLE_FIELDS`
 * in apps/api/src/routes/company-change-fields.ts). `intake.test.ts` reads
 * that file off disk and pins the names, the labels and the order against it
 * (#733). The customer bundle carries its own pin rather than relying on this
 * one, because a staff-only edit here would be invisible to a test that only
 * ever read the portal's copy.
 */
export const COMPANY_FIELD_ORDER = [
  'legalName', 'dbaName', 'businessAddress',
  'businessContact', 'supportContact', 'escalationContact',
  // #711 — last, matching FIELD_ORDER in the customer's own
  // apps/portal/src/api/companyChange.ts. The reviewer and the customer look at
  // the same request, and reading its fields in a different sequence on the two
  // screens is the small confusion that turns into a phone call. Both are held
  // to the API's order by the disk-read pin named above (#733), so this
  // position is checked rather than kept in step by hand.
  'billingContact',
  // #768 — last on both screens for the same reason.
  'callLogoUrl',
] as const;

/**
 * The parts of a billing contact, in the order the review card renders them.
 *
 * SEVEN, and the four address parts are the reason this list exists at all.
 * `formatCompanyFieldValue` used to render every non-address field as
 * name/email/phone, which for this field would silently drop the street, the
 * city, the state and the ZIP — the half of the ask that #685 split out of one
 * free-text box because nothing checked it carried a city.
 *
 * Pinned to the API rather than kept in step by hand: `intake.test.ts`
 * extracts `billingContactSchema`'s property order from
 * apps/api/src/routes/company-change-fields.ts and compares this list to it in
 * order (#733), and checks that a value carrying all seven still renders all
 * seven through `formatCompanyFieldValue` below.
 */
export const COMPANY_BILLING_PARTS = [
  'name', 'email', 'phone', 'street', 'city', 'state', 'zip',
] as const;

export const COMPANY_FIELD_LABEL: Record<string, string> = {
  legalName: 'Registered legal name',
  dbaName: 'Trading / display name',
  businessAddress: 'Business address',
  businessContact: 'Business contact',
  supportContact: 'Support contact',
  escalationContact: 'Escalation contact',
  billingContact: 'Billing contact',
  callLogoUrl: 'Call logo URL',
};

/**
 * One requested value as copy-friendly text.
 *
 * Copy-friendly is still the requirement even though #478 made accepting write
 * the cases itself: a re-vet field's value is verified by an analyst reading it
 * off this request against a document, so it has to be selectable text rather
 * than a nested structure they have to reassemble by eye.
 */
export function formatCompanyFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);
  const v = value as Record<string, string | undefined>;
  const order: readonly string[] = field === 'businessAddress'
    ? ['line1', 'line2', 'city', 'state', 'postalCode', 'country']
    // #711 — the billing contact carries an address as well as a person, so it
    // gets its own key list rather than the three-part contact default. A field
    // rendered through the wrong list loses every key the list does not name,
    // silently and looking complete.
    : field === 'billingContact'
      ? COMPANY_BILLING_PARTS
      : ['name', 'email', 'phone'];
  return order
    .map((k) => v[k])
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .join(field === 'businessAddress' ? ', ' : ' · ');
}
